import React from 'react';
import { Menu } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '../../../components/ui/_cn';

// One dropdown for the Finance pages (spec 2026-09-21 §3.3): the Payouts
// filter bar, the Order Reports store select and the period picker's centre.
// headlessui's Menu rather than antd's Select, because the mock's trigger is
// an outlined button and antd's combobox cannot be made to look like one.
//
// `radio` holds one value; `checkbox` holds an array and toggles. The
// trigger reads `label` when given (the filter bar computes "Status · 2"),
// else the selected option's label (the store select, the period preset).
const SIZES = {
  sm: 'text-[13px] text-fin-ink-2',
  md: 'text-[13.5px] text-fin-ink',
};

// The Coupons modals (spec 2026-10-21 §2.4) add four looks, all off by
// default: `placeholder` (drawn faint while nothing is chosen), `disabled`,
// `block` (a full-width field: Store, Menus) and `chip` (the rule
// sentence's 40px blue value chip; with `bare`, one half of a joined chip
// whose ground the parent draws).
const CHIP = 'h-10 px-3 text-[19px] font-bold text-fin-blue-deep';

const FinDropdown = ({
  label,
  value,
  options = [],
  mode = 'radio',
  onChange,
  active = false,
  size = 'sm',
  bare = false,
  placeholder = '',
  disabled = false,
  block = false,
  chip = false,
  'aria-label': ariaLabel,
}) => {
  const isChecked = (option) =>
    mode === 'checkbox' ? (value || []).includes(option.value) : value === option.value;
  const selected = options.find((option) => option.value === value);
  const text = label ?? selected?.label ?? '';

  const pick = (option) => {
    if (mode === 'checkbox') {
      const current = value || [];
      onChange(
        current.includes(option.value)
          ? current.filter((v) => v !== option.value)
          : [...current, option.value],
      );
      return;
    }
    onChange(option.value);
  };

  return (
    <Menu as="div" className={cn('relative', block ? 'block w-full' : 'inline-block')}>
      <Menu.Button
        aria-label={ariaLabel}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-1.5 whitespace-nowrap',
          chip
            ? cn(CHIP, !bare && 'rounded-lg bg-fin-selected', !disabled && 'hover:bg-fin-blue-pale')
            : cn(
                'px-3 py-[9px] font-semibold bg-fin-surface hover:bg-fin-recess',
                SIZES[size] || SIZES.sm,
                !bare && 'rounded-lg border',
                !bare && (active ? 'border-fin-blue text-fin-blue' : 'border-fin-border-2'),
                bare && active && 'text-fin-blue',
              ),
          block && 'w-full justify-between',
          disabled && 'cursor-default opacity-60',
        )}
      >
        {text ? (
          block ? <span className="truncate">{text}</span> : text
        ) : (
          <span className={cn('truncate', chip ? 'text-fin-blue-deep/60' : 'font-medium text-fin-faint')}>{placeholder}</span>
        )}
        <ChevronDownIcon className="h-3 w-3 shrink-0 stroke-[2.5]" aria-hidden="true" />
      </Menu.Button>
      <Menu.Items
        className={cn(
          'absolute left-0 z-20 mt-1 min-w-[180px] max-h-64 overflow-y-auto rounded-lg border border-fin-border bg-fin-surface shadow-none p-1 focus:outline-none',
          block && 'w-full',
        )}
      >
        {options.map((option) => {
          const checked = isChecked(option);
          return (
            <Menu.Item key={option.value}>
              {({ active: hovered }) => (
                <button
                  type="button"
                  aria-checked={checked}
                  onClick={(event) => {
                    // Checkbox mode toggles one status at a time, so the menu
                    // must stay open across picks. headlessui's Menu.Item
                    // merges our onClick ahead of its own close-on-click
                    // handler and skips the rest once the event is marked
                    // defaultPrevented (see @headlessui/react's
                    // utils/render.js `mergeProps`) — radio mode still
                    // closes, since a single pick finishes the choice.
                    if (mode === 'checkbox') event.preventDefault();
                    pick(option);
                  }}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-[13px] text-fin-ink',
                    hovered && 'bg-fin-hover',
                  )}
                >
                  {mode === 'checkbox' ? (
                    <span
                      className={cn(
                        'flex h-3.5 w-3.5 items-center justify-center rounded border',
                        checked ? 'border-fin-blue bg-fin-blue' : 'border-fin-border-2 bg-fin-surface',
                      )}
                      aria-hidden="true"
                    >
                      {checked ? <CheckIcon className="h-2.5 w-2.5 stroke-[3] text-white" /> : null}
                    </span>
                  ) : (
                    <CheckIcon
                      className={cn('h-3.5 w-3.5 stroke-[2.5] text-fin-blue', !checked && 'invisible')}
                      aria-hidden="true"
                    />
                  )}
                  {option.label}
                </button>
              )}
            </Menu.Item>
          );
        })}
      </Menu.Items>
    </Menu>
  );
};

export default FinDropdown;
